import type { ChannelIdentityType, IChannelIdentityConnection, IChannelIdentityView } from './marketing-channels.service'

export const IDENTITY_CHANNEL_IDS = ['linkedin']

const DAY_MS = 24 * 60 * 60 * 1000

export type IdentityRowState = 'not-connected' | 'connected' | 'expired'
export type IdentityRowAction = 'connect' | 'reconnect' | 'remove'
export type IdentityRowTone = 'ok' | 'error' | 'warning' | 'none'
export type IdentityChipTone = 'ok' | 'partial' | 'off' | 'error'

export interface IdentityRowView {
  identityType: ChannelIdentityType
  name: string
  named: boolean
  initials: string
  state: IdentityRowState
  tone: IdentityRowTone
  icon: string
  tooltip: string
  action: IdentityRowAction
  actionLabel: string
  actionDisabled: boolean
}

export interface IdentityChip {
  label: string
  tone: IdentityChipTone
}

export interface ChannelIdentityCardView {
  channelId: string
  chip: IdentityChip
  caption: string
  rows: IdentityRowView[]
}

export interface IdentityCardContext {
  identity?: IChannelIdentityView
  now: number
  connectingKeys?: ReadonlySet<string>
  removingKeys?: ReadonlySet<string>
}

export function identityKey(channelId: string, identityType: ChannelIdentityType): string {
  return `${channelId}:${identityType}`
}

export function hasPendingConnect(connectingKeys: ReadonlySet<string> | undefined, channelId: string): boolean {
  if (!connectingKeys) return false
  const prefix = `${channelId}:`
  for (const key of connectingKeys) if (key.startsWith(prefix)) return true
  return false
}

export function hasIdentityRows(channelId: string): boolean {
  return IDENTITY_CHANNEL_IDS.includes(channelId)
}

export function formatShortDate(epochMs?: number): string {
  if (!epochMs) return ''
  return new Date(epochMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function daysUntil(expiresAt: number | undefined, now: number): number | undefined {
  if (!expiresAt) return undefined
  return Math.ceil((expiresAt - now) / DAY_MS)
}

// A `disconnected` connection carrying a `lastError` was revoked at the provider rather than by the
// user, so it reads as expired and needs a reconnect.
export function identityRowState(connection: IChannelIdentityConnection | undefined, now: number): 'not-connected' | 'connected' | 'expired' {
  if (!connection) return 'not-connected'
  if (connection.status === 'connected') {
    const remaining = daysUntil(connection.expiresAt, now)
    return remaining !== undefined && remaining <= 0 ? 'expired' : 'connected'
  }
  if (connection.status === 'disconnected' && connection.lastError) return 'expired'
  return 'not-connected'
}

/** Either identity live is enough — the composer offers whichever author is usable; an expired one reads as not connected. */
export function isIdentityConnected(identities: IChannelIdentityView[], channelId: string, now: number): boolean {
  const view = identities.find(identity => identity.channelId === channelId)
  return identityRowState(view?.member, now) === 'connected' || identityRowState(view?.company, now) === 'connected'
}

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (!words.length) return ''
  const initials = words.length > 1 ? `${words[0][0]}${words[1][0]}` : words[0].slice(0, 2)
  return initials.toUpperCase()
}

function connectedMeta(connection: IChannelIdentityConnection, now: number): string {
  const connected = formatShortDate(connection.connectedAt)
  const remaining = daysUntil(connection.expiresAt, now)
  const renews = remaining === undefined ? '' : `renews in ${remaining} ${remaining === 1 ? 'day' : 'days'}`
  return [connected && `Connected ${connected}`, renews].filter(Boolean).join(' · ')
}

function tooltip(title: string, detail: string, meta?: string): string {
  return [title, detail, meta].filter(Boolean).join(' — ')
}

function memberRow(identity: IChannelIdentityView | undefined, now: number): IdentityRowView {
  const connection = identity?.member
  const state = identityRowState(connection, now)
  const teammates = identity?.teammates?.connectedCount ?? 0
  const teammateNote = teammates ? ` ${teammates} ${teammates === 1 ? 'teammate has' : 'teammates have'} connected their own account.` : ''

  if (state === 'connected') {
    const name = connection?.displayName || 'Your account'
    return {
      identityType: 'member',
      name,
      named: !!connection?.displayName,
      initials: initialsOf(name),
      state,
      tone: 'ok',
      icon: 'check',
      tooltip: tooltip(
        'Connected',
        `Your personal LinkedIn account. Posts publish under your name, to your own feed.${teammateNote}`,
        connectedMeta(connection!, now)
      ),
      action: 'remove',
      actionLabel: 'Remove',
      actionDisabled: false,
    }
  }

  if (state === 'expired') {
    const expiredOn = formatShortDate(connection?.disconnectedAt ?? connection?.expiresAt)
    return {
      identityType: 'member',
      name: connection?.displayName || 'Your account',
      named: !!connection?.displayName,
      initials: initialsOf(connection?.displayName || 'Your account'),
      state,
      tone: 'error',
      icon: 'priority_high',
      tooltip: tooltip(
        'Access expired',
        "LinkedIn expires access every 60 days. Reconnect to keep posting as yourself — nothing you've drafted is affected.",
        expiredOn && `Expired ${expiredOn}`
      ),
      action: 'reconnect',
      actionLabel: 'Reconnect',
      actionDisabled: false,
    }
  }

  return {
    identityType: 'member',
    name: 'Your account',
    named: false,
    initials: '',
    state,
    tone: 'none',
    icon: 'add',
    tooltip: tooltip(
      'Not connected',
      `You'll authorise with LinkedIn, then land back here. Only you can connect your own account — a teammate can't do it for you.${teammateNote}`
    ),
    action: 'connect',
    actionLabel: 'Connect',
    actionDisabled: false,
  }
}

function companyRow(identity: IChannelIdentityView | undefined, now: number): IdentityRowView {
  const connection = identity?.company
  const state = identityRowState(connection, now)

  // A pending connection that already names its page is a crashed finalize, not a choice still to
  // make — re-running the authorisation is the whole fix.
  if (connection?.status === 'pending' && connection.externalId) {
    return {
      identityType: 'company',
      name: connection.displayName || 'Company page',
      named: !!connection.displayName,
      initials: initialsOf(connection.displayName || 'Company page'),
      state: 'not-connected',
      tone: 'warning',
      icon: 'priority_high',
      tooltip: tooltip('Not connected', "This connection didn't finish. Reconnect to complete it — nothing else was changed."),
      action: 'reconnect',
      actionLabel: 'Reconnect',
      actionDisabled: false,
    }
  }

  if (state === 'connected') {
    const name = connection?.displayName || 'Company page'
    const connectedOn = formatShortDate(connection?.connectedAt)
    const by = connection?.connectedByDisplayName
    const meta = by ? [`Connected by ${by}`, connectedOn].filter(Boolean).join(' · ') : connectedOn && `Connected ${connectedOn}`
    return {
      identityType: 'company',
      name,
      named: !!connection?.displayName,
      initials: initialsOf(name),
      state,
      tone: 'ok',
      icon: 'check',
      tooltip: tooltip('Connected', `The ${name} company page. Shared with everyone here — posts publish as the company, not as you.`, meta),
      action: 'remove',
      actionLabel: 'Remove',
      actionDisabled: false,
    }
  }

  if (state === 'expired') {
    const expiredOn = formatShortDate(connection?.disconnectedAt ?? connection?.expiresAt)
    return {
      identityType: 'company',
      name: connection?.displayName || 'Company page',
      named: !!connection?.displayName,
      initials: initialsOf(connection?.displayName || 'Company page'),
      state,
      tone: 'error',
      icon: 'priority_high',
      tooltip: tooltip(
        'Access expired',
        'LinkedIn expires access every 60 days. Reconnect to keep posting as the company page — anyone here with page admin rights can do it.',
        expiredOn && `Expired ${expiredOn}`
      ),
      action: 'reconnect',
      actionLabel: 'Reconnect',
      actionDisabled: false,
    }
  }

  return {
    identityType: 'company',
    name: 'Company page',
    named: false,
    initials: '',
    state,
    tone: 'none',
    icon: 'add',
    tooltip: tooltip(
      'Not connected',
      "Any page admin for your company can connect this. Once it's connected, everyone here can post as the company."
    ),
    action: 'connect',
    actionLabel: 'Connect',
    actionDisabled: false,
  }
}

function applyBusy(channelId: string, row: IdentityRowView, ctx: IdentityCardContext): IdentityRowView {
  const key = identityKey(channelId, row.identityType)
  if (ctx.connectingKeys?.has(key)) return { ...row, actionLabel: 'Connecting…', actionDisabled: true }
  if (ctx.removingKeys?.has(key)) return { ...row, actionLabel: 'Removing…', actionDisabled: true }
  const connectAction = row.action === 'connect' || row.action === 'reconnect'
  if (connectAction && hasPendingConnect(ctx.connectingKeys, channelId)) return { ...row, actionDisabled: true }
  return row
}

function chipFor(rows: IdentityRowView[]): IdentityChip {
  if (rows.some(r => r.state === 'expired')) return { label: 'Needs attention', tone: 'error' }
  const connected = rows.filter(r => r.state === 'connected').length
  if (connected === rows.length) return { label: 'Connected', tone: 'ok' }
  if (connected === 0) return { label: 'Disconnected', tone: 'off' }
  return { label: `${connected} of ${rows.length} connected`, tone: 'partial' }
}

function captionFor(member: IdentityRowView, company: IdentityRowView, identity?: IChannelIdentityView): string {
  if (member.state === 'expired' && company.state !== 'expired') return 'Only your account needs reconnecting'
  if (company.state === 'expired' && member.state !== 'expired') return 'Only the company page needs reconnecting'
  if (member.state === 'expired' && company.state === 'expired') return 'Both connections need reconnecting'
  if (company.state === 'connected') {
    const by = identity?.company?.connectedByDisplayName
    return by ? `Page connected by ${by}` : 'Company page connected'
  }
  return 'Each is authorized separately'
}

export function buildIdentityCardView(card: { id: string; locked: boolean }, ctx: IdentityCardContext): ChannelIdentityCardView | undefined {
  if (card.locked || !hasIdentityRows(card.id)) return undefined
  const member = applyBusy(card.id, memberRow(ctx.identity, ctx.now), ctx)
  const company = applyBusy(card.id, companyRow(ctx.identity, ctx.now), ctx)
  const rows = [member, company]
  return { channelId: card.id, chip: chipFor(rows), caption: captionFor(member, company, ctx.identity), rows }
}

const IDENTITY_HANDOFF_PREFIX = 'channel-connect-identity:'

export function rememberConnectIdentity(storage: Pick<Storage, 'setItem'>, channelId: string, identityType: ChannelIdentityType): void {
  storage.setItem(`${IDENTITY_HANDOFF_PREFIX}${channelId}`, identityType)
}

export function takeConnectIdentity(storage: Pick<Storage, 'getItem' | 'removeItem'>, channelId: string): ChannelIdentityType | undefined {
  const key = `${IDENTITY_HANDOFF_PREFIX}${channelId}`
  const value = storage.getItem(key)
  storage.removeItem(key)
  return value === 'member' || value === 'company' ? value : undefined
}

export function completeConnectPayload(
  code: string,
  state: string | undefined,
  redirectUri: string,
  identityType?: ChannelIdentityType
): Record<string, unknown> {
  return identityType ? { code, state, redirectUri, identityType } : { code, state, redirectUri }
}

// The single-use `state` issued by begin lives for 10 minutes; past that the callback must restart
// from begin rather than retry complete.
export const CHANNEL_STATE_MISMATCH_ERROR = 'jobs-integration/oauth-state-validation-failed'
export const LINKEDIN_AUTH_EXPIRED_ERROR = 'jobs-integration/linkedin-auth-expired'

interface MaybeE11Error {
  errorData?: { type?: string }
  response?: { data?: { type?: unknown } }
}

// The rest client copies the response body onto errorData only when it carries a `type`.
export function channelErrorType(err: unknown): string {
  const e = err as MaybeE11Error
  const type = e?.errorData?.type ?? e?.response?.data?.type
  return typeof type === 'string' ? type : ''
}

export type ConnectFailure = 'state-mismatch' | 'auth-expired' | 'generic'

export function classifyConnectError(err: unknown): ConnectFailure {
  const type = channelErrorType(err)
  if (type === CHANNEL_STATE_MISMATCH_ERROR) return 'state-mismatch'
  if (type === LINKEDIN_AUTH_EXPIRED_ERROR) return 'auth-expired'
  return 'generic'
}

export function connectFailureMessage(failure: ConnectFailure): string {
  if (failure === 'state-mismatch') return 'That connection attempt expired before it finished. Please start the connection again.'
  if (failure === 'auth-expired') return 'LinkedIn ended the session before we could finish. Please connect again.'
  return 'We could not complete the channel connection. Please try again.'
}
